/**
 * Shared types for the SPS Commerce shipping label PDF splitter.
 * These types are used by the browser pipeline, the CLI test harness,
 * the local dev server and the Cloudflare Worker (keep them dependency-free).
 */

export type JobStatus =
  | 'uploaded'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_warnings'
  | 'failed';

export type PageStatus = 'success' | 'needs_review' | 'unresolved';

export type OutputType =
  | 'single_item_single_piece'
  | 'single_item_multi_piece'
  | 'multi_item_mixed'
  | 'po_group'
  | 'tracking_group';

export type OutputStatus = 'success' | 'needs_review' | 'failed';

export interface TrackingCandidate {
  value: string;
  corrected: boolean;
  fromWords: boolean;
  confidence: number | null;
}

/** Per-page recognition result. */
export interface PageResult {
  pageNumber: number; // 1-based, original page number in the source PDF
  tracking: string | null; // normalized + corrected tracking number (UPS format)
  trackingRaw: string | null; // raw OCR string as read
  trackingCandidates: string[]; // every candidate found on the page
  trackingConfidence: number | null;
  trackingCorrected: boolean;
  po: string | null;
  poRaw: string | null;
  orderIndicator: string | null; // e.g. "1 OF 1"
  status: PageStatus;
  error?: string;
  warnings: string[];
}

/** One output PDF. */
export interface OutputGroup {
  fileName: string; // final file name, e.g. "1ZY23D260333348330.pdf"
  trackingNumber: string | null; // null for the aggregate / unresolved file
  orderNumbers: string[]; // distinct PO / Trx Ref numbers
  pageNumbers: number[]; // 1-based, ascending, original page order
  type: OutputType;
  status: OutputStatus;
  warnings: string[];
}

export interface SplitSummary {
  sourceFileName: string;
  sourcePageCount: number;
  processedPageCount: number;
  unresolvedPageCount: number;
  singlePageCount: number; // pages in the aggregate single PDF
  poGroupCount: number; // multi-piece / mixed order PDFs grouped by PO
  outputFileCount: number; // total generated PDFs (incl. unresolved)
  needsReviewCount: number;
  elapsedMs: number;
  trackingRecognitionRate: number; // 0..1
  pageConservationOk: boolean;
  warnings: string[];
}

/** Job record persisted by the backend (Cloudflare D1 / local disk). */
export interface JobRecord {
  jobId: string;
  sourceFileName: string;
  sourceBaseName: string;
  sourcePageCount: number;
  sourceSizeBytes: number;
  status: JobStatus;
  maxFileSizeBytes: number;
  expiresAt: string; // ISO timestamp
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  outputs: OutputGroup[];
  unresolvedPages: number[];
  pageResults: PageResult[];
  summary?: SplitSummary;
  report?: string; // JSON report content
  error?: string;
}

export interface CreateJobRequest {
  fileName: string;
  sizeBytes: number;
}

export interface CreateJobResponse {
  jobId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface UploadTicket {
  jobId: string;
  url: string;
  method: string;
  headers: Record<string, string>;
}

export interface OutputUploadTicket extends UploadTicket {
  fileName: string;
}

export interface CompleteJobRequest {
  sourcePageCount: number;
  outputs: OutputGroup[];
  unresolvedPages: number[];
  pageResults: PageResult[];
  summary: SplitSummary;
  report: string;
}

export const TRACKING_RE = /^1Z[A-Z0-9]{16}$/;
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB
export const MAX_CONCURRENT_JOBS = 5;
export const RETENTION_DAYS = 30;

/** Fixed label regions (fractions of the page size) used by the OCR pass. */
export const LABEL_REGIONS = {
  /** Region containing the "TRACKING #:" line. */
  tracking: { x: 0, y: 0.4, w: 0.98, h: 0.42 },
  /** Region containing "Trx Ref No.:" at the bottom-left. */
  po: { x: 0, y: 0.78, w: 0.72, h: 0.22 },
  /** Top banner containing the "x OF y" package indicator. */
  banner: { x: 0, y: 0, w: 0.9, h: 0.2 },
  /** The "x OF y" package indicator (top-right corner). */
  indicator: { x: 0.76, y: 0, w: 0.24, h: 0.08 },
} as const;
